/**
 * Dell PowerEdge R760xa Server Profile
 *
 * Source: research/server-specs.md
 * - 2U form factor
 * - Up to 4 double-width or 12 single-width PCIe GPUs
 * - Dual Intel Xeon Scalable (up to 64 cores each)
 * - Up to 8TB DDR5
 * - Flexible PSU options (2400W, 2800W, 3200W)
 * - Air-cooled
 */

import type { ServerProfile } from '../../types/servers';

/**
 * Dell PowerEdge R760xa with RTX 6000 Ada (4-GPU)
 */
export const DELL_R760XA_RTX6000: ServerProfile = {
  id: 'dell-r760xa-rtx6000',
  name: 'Dell PowerEdge R760xa (RTX 6000 Ada)',
  role: 'compute',
  vendor: 'Dell',
  model: 'PowerEdge R760xa',

  cpu: {
    sockets: 2,
    coresPerSocket: 32,
    model: 'Intel Xeon Gold 6542Y (5th Gen)',
    tdpPerSocketWatts: 250,
    baseClockGHz: 2.9,
    turboClockGHz: 4.0,
  },

  memory: {
    totalGB: 512,
    channels: 16,
    type: 'DDR5',
    speedMTs: 5600,
    dimmCount: 16,
    capacityPerDimmGB: 32,
  },

  gpu: {
    platformId: 'rtx-6000-ada',
    gpusPerNode: 4,
  },

  expansion: {
    pcieSlots: [
      // 4 rear slots for GPUs
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      // 4 front slots for networking/storage
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
    ],
    m2Slots: 2,
    drive25Count: 4,
    drive35Count: 0,
  },

  network: {
    onboard1GbE: 2,
    onboard10GbE: 0,
    onboard25GbE: 0,
    bmcPorts: 1,
  },

  power: {
    maxPowerWatts: 4800,
    typicalPowerWatts: 2000,
    idlePowerWatts: 200,
    redundancy: 'N+1',
    psuCount: 2,
    psuCapacityWatts: 2400,
    efficiency: '80 PLUS Platinum',
    inputVoltage: '200-240V AC',
  },

  dimensions: {
    rackUnits: 2,
    widthMm: 482.6,
    depthMm: 730,
    heightMm: 87,
    weightKg: 35,
    maxAltitudeMeters: 3048,
  },

  environmental: {
    tempRange: { min: 10, max: 35 },
    humidityRange: { min: 10, max: 80 },
    maxBtuPerHour: 6824, // 2000W * 3.412
    coolingType: 'air',
    noiseLevelDbA: 68,
  },

  metadata: {
    productUrl: 'https://www.dell.com/en-us/shop/ipovw/poweredge-r760xa',
    notes: '4x RTX 6000 Ada configuration for professional visualization and AI inference',
    validated: true,
  },
} as const;

/**
 * Dell PowerEdge R760xa with L40S (4-GPU)
 */
export const DELL_R760XA_L40S: ServerProfile = {
  id: 'dell-r760xa-l40s',
  name: 'Dell PowerEdge R760xa (L40S)',
  role: 'compute',
  vendor: 'Dell',
  model: 'PowerEdge R760xa',

  cpu: {
    sockets: 2,
    coresPerSocket: 32,
    model: 'Intel Xeon Gold 6542Y (5th Gen)',
    tdpPerSocketWatts: 250,
    baseClockGHz: 2.9,
    turboClockGHz: 4.0,
  },

  memory: {
    totalGB: 1024,
    channels: 16,
    type: 'DDR5',
    speedMTs: 5600,
    dimmCount: 32,
    capacityPerDimmGB: 32,
  },

  gpu: {
    platformId: 'l40s',
    gpusPerNode: 4,
  },

  expansion: {
    pcieSlots: [
      // 4 rear slots for GPUs
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      // 4 front slots for networking/storage
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
    ],
    m2Slots: 2,
    drive25Count: 8,
    drive35Count: 0,
  },

  network: {
    onboard1GbE: 2,
    onboard10GbE: 0,
    onboard25GbE: 0,
    bmcPorts: 1,
  },

  power: {
    maxPowerWatts: 5600,
    typicalPowerWatts: 2000,
    idlePowerWatts: 200,
    redundancy: 'N+1',
    psuCount: 2,
    psuCapacityWatts: 2800,
    efficiency: '80 PLUS Titanium',
    inputVoltage: '200-240V AC',
  },

  dimensions: {
    rackUnits: 2,
    widthMm: 482.6,
    depthMm: 730,
    heightMm: 87,
    weightKg: 38,
    maxAltitudeMeters: 3048,
  },

  environmental: {
    tempRange: { min: 10, max: 35 },
    humidityRange: { min: 10, max: 80 },
    maxBtuPerHour: 6824, // 2000W * 3.412
    coolingType: 'air',
    noiseLevelDbA: 70,
  },

  metadata: {
    productUrl: 'https://www.dell.com/en-us/shop/ipovw/poweredge-r760xa',
    notes: '4x L40S configuration for AI inference and graphics workloads',
    validated: true,
  },
} as const;
